import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Mail, Phone, MapPin, ShoppingBag, DollarSign, TrendingUp, Send, Loader2, Clock, User } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { fmt, fmtDate, fmtDateTime, StatusBadge, Card, Spinner } from "@/components/admin/shared";

const Panel = ({ title, action, children }) => (
  <div className="rounded-2xl border border-[#E5DBC8] bg-white p-5">
    <div className="mb-3 flex items-center justify-between">
      <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">{title}</p>
      {action}
    </div>
    {children}
  </div>
);

function CustomerProfile({ email, onClose }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get(`/admin/customers/${encodeURIComponent(email)}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!data) return <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40"><Spinner /></div>;

  const cust = data.customer || {};
  const addr = cust.address || {};

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await adminApi.post(`/admin/customers/${encodeURIComponent(email)}/note`, { text: note }); setNote(""); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const saveTags = async (tags) => {
    try { await adminApi.post(`/admin/customers/${encodeURIComponent(email)}/tags`, { tags }); setData((d) => ({ ...d, tags })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
  };
  const addTag = () => { const t = tagInput.trim(); if (t && !(data.tags || []).includes(t)) saveTags([...(data.tags || []), t]); setTagInput(""); };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-[#F1EEE9]" data-testid="customer-profile">
      <div className="mx-auto max-w-5xl px-5 py-6 md:px-8">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={onClose} data-testid="customer-profile-close" className="rounded-full border border-[#DACDB4] p-1.5 text-[#6E6168] hover:bg-white"><X className="h-4 w-4" /></button>
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F4A97F] font-display text-lg text-white">{(cust.name || email)[0].toUpperCase()}</div>
          <div>
            <h1 className="font-display text-2xl text-[#2C2B2C]">{cust.name || email}</h1>
            <p className="text-xs text-[#8A7F86]">{addr.suburb ? `${addr.suburb}, ${addr.state} · ` : ""}Customer since {fmtDate(data.first_order)}</p>
          </div>
        </div>

        <div className="mb-5 grid grid-cols-3 gap-4">
          <Card className="!p-5"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFE7D6] text-[#FBC4A8]"><ShoppingBag className="h-4 w-4" /></span><p className="mt-3 font-display text-2xl text-[#2C2B2C]">{data.orders_count}</p><p className="text-xs uppercase tracking-wider text-[#8A7F86]">Orders</p></Card>
          <Card className="!p-5"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFE7D6] text-[#FBC4A8]"><DollarSign className="h-4 w-4" /></span><p className="mt-3 font-display text-2xl text-[#2C2B2C]">{fmt(data.total_spent)}</p><p className="text-xs uppercase tracking-wider text-[#8A7F86]">Lifetime spend</p></Card>
          <Card className="!p-5"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFE7D6] text-[#FBC4A8]"><TrendingUp className="h-4 w-4" /></span><p className="mt-3 font-display text-2xl text-[#2C2B2C]">{fmt(data.aov)}</p><p className="text-xs uppercase tracking-wider text-[#8A7F86]">Avg order value</p></Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.5fr_1fr]">
          <div className="space-y-5">
            <Panel title="Order history">
              <div className="space-y-3" data-testid="customer-orders">
                {data.orders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between rounded-xl border border-[#EDE4D4] px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-[#2C2B2C]">#{o.id.slice(0, 8).toUpperCase()}</p>
                      <p className="text-xs text-[#8A7F86]">{fmtDateTime(o.created_at)} · {(o.items || []).reduce((s, i) => s + i.quantity, 0)} item(s)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={o.payment_status} />
                      <span className="w-16 text-right font-display text-base text-[#2C2B2C]">{fmt(o.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="Timeline">
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} data-testid="customer-note-input"
                  placeholder="Leave a note about this customer…" className="flex-1 rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                <button onClick={addNote} disabled={busy || !note.trim()} data-testid="customer-note-post"
                  className="flex items-center gap-1.5 rounded-full bg-[#232222] px-4 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </button>
              </div>
              <div className="mt-4 space-y-3" data-testid="customer-timeline">
                {(data.timeline || []).map((ev, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 flex-none rounded-full bg-[#FBC4A8]" />
                    <div><p className="text-sm text-[#2C2B2C]">{ev.author ? <span className="font-medium">{ev.author}: </span> : null}{ev.message}</p><p className="text-[0.68rem] text-[#B6A99F]">{fmtDateTime(ev.at)}</p></div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="space-y-5">
            <Panel title="Contact">
              <p className="flex items-center gap-2 text-sm text-[#3B6FB0]"><Mail className="h-4 w-4 text-[#FBC4A8]" /> {email}</p>
              {cust.phone && <p className="mt-2 flex items-center gap-2 text-sm text-[#6E6168]"><Phone className="h-4 w-4 text-[#FBC4A8]" /> {cust.phone}</p>}
            </Panel>
            <Panel title="Default address">
              {addr.line1 ? (
                <div className="text-sm text-[#6E6168]">
                  <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 flex-none text-[#FBC4A8]" /><span>{cust.name}<br />{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}<br />{addr.suburb} {addr.state} {addr.postcode}<br />{addr.country === "AU" ? "Australia" : addr.country}</span></p>
                </div>
              ) : <p className="text-sm text-[#B6A99F]">No saved address.</p>}
            </Panel>
            <Panel title="Tags">
              <div className="flex flex-wrap gap-2" data-testid="customer-tags">
                {(data.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-[#F2E8E6] px-3 py-1 text-xs text-[#2C2B2C]">{t}<button onClick={() => saveTags((data.tags || []).filter((x) => x !== t))}><X className="h-3 w-3" /></button></span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} data-testid="customer-tag-input"
                  placeholder="Add tag" className="flex-1 rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                <button onClick={addTag} data-testid="customer-tag-add" className="rounded-full border border-[#DACDB4] px-4 text-[0.62rem] uppercase tracking-wider text-[#6E6168]">Add</button>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => { adminApi.get("/admin/customers").then((r) => setCustomers(r.data)).finally(() => setLoading(false)); }, []);
  if (loading) return <Spinner />;

  return (
    <div data-testid="customers-panel">
      <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Customers</h1>
      <div className="mt-6 overflow-hidden rounded-3xl border border-[#E5DBC8] bg-white">
        {customers.length === 0 ? <p className="p-10 text-center text-[#8A7F86]">No customers yet. They'll appear here after the first checkout with details.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-[#E5DBC8] text-[0.6rem] uppercase tracking-wider text-[#8A7F86]">
                <tr><th className="px-5 py-4">Customer</th><th className="px-5 py-4">Location</th><th className="px-5 py-4">Orders</th><th className="px-5 py-4">Lifetime spend</th><th className="px-5 py-4">Last order</th></tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.email} onClick={() => setActive(c.email)} data-testid={`customer-row-${c.email}`}
                    className="cursor-pointer border-b border-[#EDE4D4] transition-colors last:border-0 hover:bg-[#FBF7F3]">
                    <td className="px-5 py-4"><p className="text-[#2C2B2C]">{c.name || "—"}</p><p className="text-xs text-[#8A7F86]">{c.email}</p></td>
                    <td className="px-5 py-4 text-[#8A7F86]">{c.city ? `${c.city}, ${c.state}` : "—"}</td>
                    <td className="px-5 py-4 text-[#2C2B2C]">{c.orders_count}</td>
                    <td className="px-5 py-4 font-medium text-[#2C2B2C]">{fmt(c.total_spent)}</td>
                    <td className="px-5 py-4 text-[#8A7F86]">{fmtDate(c.last_order)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {active && <CustomerProfile email={active} onClose={() => setActive(null)} />}
    </div>
  );
}
